import { useQueryClient } from '@tanstack/react-query';
import type { Granularity, Origin } from '../api/types';
import { useFilters } from '../filters/FiltersContext';
import { FilterSelect } from './FilterSelect';
import { TimeRangeSelector } from './TimeRangeSelector';

interface Props {
  /** Whether at least one query is currently fetching (drives spinner copy). */
  isRefreshing?: boolean;
  /** React Query keys to invalidate when refresh is clicked. */
  invalidateKeys: string[];
  /**
   * Whether to show the time-granularity (1h/1d/1m) dropdown. Default false
   * because only the Overview tab's time-series charts respect it.
   */
  showGranularity?: boolean;
}

const ORIGIN_OPTIONS = [
  { value: 'all', label: 'All', hint: 'Every authenticated user' },
  { value: 'adobe', label: 'Adobe', hint: 'IMS sub ends with @adobe.com (Adobe employees)' },
  {
    value: 'non-adobe',
    label: 'Non-Adobe',
    hint: 'Federated tenants (partners, customers using SLICC)',
  },
] as const satisfies readonly { value: Origin; label: string; hint: string }[];

const GRANULARITY_OPTIONS = [
  { value: 'hour', label: '1h', hint: 'Bucket time-series by hour' },
  { value: 'day', label: '1d', hint: 'Bucket time-series by day (default)' },
  { value: 'month', label: '1m', hint: 'Bucket time-series by calendar month' },
] as const satisfies readonly { value: Granularity; label: string; hint: string }[];

/**
 * Shared header strip — time-range chips + filter dropdowns + refresh button.
 * Used by Overview and Sessions; the parent supplies the React Query keys to
 * invalidate so refresh works across whatever the page fetches.
 */
export function FiltersBar({
  isRefreshing,
  invalidateKeys,
  showGranularity = false,
}: Props): JSX.Element {
  const queryClient = useQueryClient();
  const { presetId, range, origin, granularity, setRange, setOrigin, setGranularity } =
    useFilters();

  return (
    <div className="overview-header">
      <TimeRangeSelector
        selectedPresetId={presetId}
        range={range}
        onChange={(nextRange, nextPresetId) => setRange(nextRange, nextPresetId)}
      />
      <div className="filter-controls">
        <FilterSelect
          label="Users"
          value={origin}
          options={ORIGIN_OPTIONS}
          onChange={setOrigin}
        />
        {showGranularity && (
          <FilterSelect
            label="Bucket"
            value={granularity}
            options={GRANULARITY_OPTIONS}
            onChange={setGranularity}
          />
        )}
      </div>
      <button
        type="button"
        className="refresh-button"
        disabled={isRefreshing}
        onClick={() => {
          for (const key of invalidateKeys) {
            queryClient.invalidateQueries({ queryKey: [key] });
          }
        }}
        title="Refresh now"
      >
        {isRefreshing ? '⟳ Loading…' : '⟳ Refresh'}
      </button>
    </div>
  );
}
